/**
 * Link tags a Natalie link carries as `ctx_` query params (e.g. `?ctx_division=finance`),
 * sent unchanged as optional ctxParams on the token request. natalie-api stores
 * valid fields separately under ctx, removing one prefix; context never reaches
 * the agent or transcript. Names are arbitrary, with no field mappings.
 *
 * Read from the current URL at token time: every step between the landing page and
 * the room, the sign-in redirect included, keeps the full query string on the URL.
 * natalie-api owns the name, value and count limits, so nothing is checked here
 * beyond the prefix. Values may contain personal data — never log them or put personal fields in URLs.
 */

import type { components } from './generated/natalie-api';

type CtxParams = NonNullable<components['schemas']['NatalieV2TokenRequestDto']['ctxParams']>;

/** Lowercase only: `CTX_` and `Ctx_` are not link tags. */
const CTX_PARAM_PREFIX = 'ctx_';

/**
 * The `ctx_` params in a search string, keyed by their exact names, or `undefined`
 * when there are none so the request body stays as it was without them. The first
 * occurrence of a repeated name wins. `URLSearchParams` decodes `+`/`%20` and never
 * throws on malformed percent-encoding.
 */
export const extractCtxParams = (search: string): CtxParams | undefined => {
  const ctxParams: CtxParams = {};
  for (const [name, value] of new URLSearchParams(search)) {
    if (name.startsWith(CTX_PARAM_PREFIX) && !(name in ctxParams)) ctxParams[name] = value;
  }
  return Object.keys(ctxParams).length > 0 ? ctxParams : undefined;
};
